import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import QRCode from "react-qr-code";
import "./DigitalCard.css";

const CARD_URL = "https://nehchaleye.com/dr-gurkiran-kaur";

const ACTIONS = [
  { label: "Call", href: "tel:+919711511001", primary: true, testId: "card-action-call" },
  { label: "WhatsApp", href: "https://wa.me/919711511001", primary: true, testId: "card-action-whatsapp" },
  {
    label: "Book Appointment",
    href: "https://wa.me/919711511001?text=Hello%20Dr.%20Gurkiran%20Kaur%2C%20I%20would%20like%20to%20book%20an%20appointment.",
    testId: "card-action-book-appointment",
  },
  {
    label: "Get Directions",
    href: "https://www.google.com/maps/search/?api=1&query=Nehchal+Eye+Centre",
    testId: "card-action-directions",
  },
  { label: "Instagram", href: "https://www.instagram.com/nehchaleyecentre", testId: "card-action-instagram" },
  { label: "Website", href: "https://nehchaleye.com", testId: "card-action-website" },
];

const SERVICES = [
  "Comprehensive Eye Care",
  "Advanced Cataract Surgery",
  "Oculoplasty & Aesthetic Surgery",
  "Refractive Surgery",
  "Pediatric Eye Care",
  "Glaucoma Management",
  "Diabetic Retina Screening & Management",
  "Neuro-ophthalmology",
  "Ocular Injury & Trauma Management",
  "Glasses & Contact Lenses",
];

const INFO = [
  { label: "Clinic", value: "Nehchal Eye Centre", testId: "card-info-clinic" },
  { label: "Address", value: "Sector 32, Noida, Uttar Pradesh, India", testId: "card-info-address" },
  {
    label: "Phone / WhatsApp",
    value: "+91 9711511001",
    href: "tel:+919711511001",
    testId: "card-info-phone",
  },
  {
    label: "Instagram",
    value: "@nehchaleyecentre",
    href: "https://www.instagram.com/nehchaleyecentre",
    testId: "card-info-instagram",
  },
  { label: "Website", value: "nehchaleye.com", href: "https://nehchaleye.com", testId: "card-info-website" },
];

const ease = [0.22, 1, 0.36, 1];

const reveal = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

export default function DigitalCard() {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: cardRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -28]);

  useEffect(() => {
    document.title = "Dr. Gurkiran Kaur | Nehchal Eye Centre";
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="dc-root" data-testid="digital-card-page">
      <div className="dc-wrap">
        <motion.main
          ref={cardRef}
          className="dc-card"
          data-testid="digital-card"
          initial={{ opacity: 0, y: 40, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.9, ease }}
        >
          <section className="dc-hero">
            <motion.div style={{ y: heroY }}>
              <motion.img
                className="dc-logo"
                src="/logo.jpg"
                alt="Nehchal Eye Centre logo"
                data-testid="card-logo"
                initial={{ opacity: 0, scale: 0.8, rotate: -4 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.15 }}
              />
              <motion.div
                className="dc-kicker"
                data-testid="card-kicker"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease, delay: 0.3 }}
              >
                Nehchal Eye Centre
              </motion.div>
              <div className="dc-name-mask">
                <motion.h1
                  className="dc-name"
                  data-testid="card-name"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease, delay: 0.42 }}
                >
                  Dr. Gurkiran Kaur
                </motion.h1>
              </div>
              <motion.div
                className="dc-role"
                data-testid="card-role"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease, delay: 0.6 }}
              >
                Ophthalmologist &amp; Oculoplastic Surgeon
              </motion.div>
              <motion.div
                className="dc-clinic"
                data-testid="card-designation"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease, delay: 0.72 }}
              >
                Founder &amp; Director
              </motion.div>
            </motion.div>
          </section>

          <motion.nav
            className="dc-actions"
            data-testid="card-actions"
            variants={stagger}
            initial="hidden"
            animate="show"
          >
            {ACTIONS.map((action) => (
              <motion.a
                key={action.testId}
                data-testid={action.testId}
                className={`dc-btn${action.primary ? " dc-btn-primary" : ""}`}
                href={action.href}
                target={action.href.startsWith("http") ? "_blank" : undefined}
                rel={action.href.startsWith("http") ? "noopener noreferrer" : undefined}
                variants={{
                  hidden: { opacity: 0, y: 18 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease, delay: 0.7 } },
                }}
              >
                {action.label}
              </motion.a>
            ))}
          </motion.nav>

          <motion.section
            className="dc-section"
            data-testid="card-services-section"
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-40px" }}
          >
            <motion.div className="dc-title" data-testid="card-services-title" variants={reveal}>
              Eye Care Services
            </motion.div>
            <div className="dc-services">
              {SERVICES.map((service, i) => (
                <motion.div
                  key={service}
                  className="dc-service"
                  data-testid={`card-service-${i}`}
                  variants={reveal}
                >
                  {service}
                </motion.div>
              ))}
            </div>
          </motion.section>

          <motion.section
            className="dc-info"
            data-testid="card-info-section"
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-40px" }}
          >
            {INFO.map((row) => (
              <motion.div className="dc-row" key={row.testId} variants={reveal}>
                <div className="dc-label">{row.label}</div>
                <div className="dc-value" data-testid={row.testId}>
                  {row.href ? (
                    <a href={row.href} target={row.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
                      {row.value}
                    </a>
                  ) : (
                    row.value
                  )}
                </div>
              </motion.div>
            ))}
          </motion.section>

          <motion.section
            className="dc-share"
            data-testid="card-share-section"
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-40px" }}
          >
            <motion.div className="dc-title" data-testid="card-share-title" variants={reveal}>
              Share This Card
            </motion.div>
            <motion.div className="dc-qr-frame" data-testid="card-qr-code" variants={reveal}>
              <QRCode value={CARD_URL} size={148} bgColor="#ffffff" fgColor="#172a34" />
            </motion.div>
            <motion.div className="dc-share-hint" variants={reveal}>
              Scan with any phone camera to open this card
            </motion.div>
            <motion.div className="dc-share-url" data-testid="card-share-url" variants={reveal}>
              <a href={CARD_URL} target="_blank" rel="noopener noreferrer">
                nehchaleye.com/dr-gurkiran-kaur
              </a>
            </motion.div>
            <motion.div className="dc-print-btn-wrap" variants={reveal}>
              <button
                type="button"
                className="dc-print-btn"
                data-testid="card-print-button"
                onClick={() => window.print()}
              >
                Print QR Card
              </button>
            </motion.div>
          </motion.section>

          <motion.footer
            className="dc-footer"
            data-testid="card-footer"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
          >
            <strong>Redefining Eye Care</strong>
            <small>Clearer Vision. Brighter Tomorrows.</small>
          </motion.footer>
        </motion.main>
      </div>

      <div className="dc-print-card" data-testid="card-print-layout" aria-hidden="true">
        <img className="dc-print-logo" src="/logo.jpg" alt="Nehchal Eye Centre logo" />
        <div className="dc-print-kicker">Nehchal Eye Centre</div>
        <div className="dc-print-name">Dr. Gurkiran Kaur</div>
        <div className="dc-print-role">Ophthalmologist &amp; Oculoplastic Surgeon</div>
        <div className="dc-print-qr">
          <QRCode value={CARD_URL} size={170} bgColor="#ffffff" fgColor="#172a34" />
        </div>
        <div className="dc-print-hint">Scan with any phone camera to open my digital visiting card</div>
        <div className="dc-print-url">nehchaleye.com/dr-gurkiran-kaur</div>
      </div>
    </div>
  );
}
